/**
 * Call History tab — paginated list of calls.
 * All filters (direction, status, date range, search) trigger server-side
 * Twilio API calls. No client-side filtering.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Clock,
  Loader2,
  Search,
  ChevronDown,
  Phone,
  MessageCircle,
  Calendar,
  X,
  Filter,
  Download,
  Mic,
  StickyNote,
  Tag,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCallStore } from '@/stores/call-store';
import { useUIStore } from '@/stores/ui-store';
import { useAppUsersStore } from '@/stores/app-users-store';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/use-permissions';
import { useVisibleCallerIds } from '@/hooks/use-visible-caller-ids';
import { CallDetailPanel } from './call-detail-panel';
import type { CallHistoryItem } from '@/types/voice';
import { DEFAULT_CALL_LABELS, LABEL_COLOR_PALETTE, isLiveCallStatus } from '@/types/voice';

type DirectionFilter = 'all' | 'inbound' | 'outbound';
type StatusFilter = 'all' | 'completed' | 'no-answer' | 'failed';

export function CallHistory({ compact }: { compact?: boolean } = {}) {
  const callHistory = useCallStore((s) => s.callHistory);
  const callHistoryLoading = useCallStore((s) => s.callHistoryLoading);
  const callHistoryHasMore = useCallStore((s) => s.callHistoryHasMore);
  const fetchCallHistory = useCallStore((s) => s.fetchCallHistory);
  const setDialerNumber = useCallStore((s) => s.setDialerNumber);
  const setCallManagerTab = useCallStore((s) => s.setCallManagerTab);

  const callsWithRecordings = useCallStore((s) => s.callsWithRecordings);
  const callMetadata = useCallStore((s) => s.callMetadata);
  const fetchCallMetadataBatch = useCallStore((s) => s.fetchCallMetadataBatch);
  const updateCallMetadata = useCallStore((s) => s.updateCallMetadata);
  const customCallLabels = useCallStore((s) => s.customCallLabels);
  const fetchCustomCallLabels = useCallStore((s) => s.fetchCustomCallLabels);
  const saveCustomCallLabel = useCallStore((s) => s.saveCustomCallLabel);
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);
  const currentUserEmail = useAuthStore((s) => s.user?.email);
  const isAdmin = !selectedAdminUserId;
  const api = useCallStore((s) => s.api);
  const accountSid = useCallStore((s) => s.accountSid);
  const { callPermissions } = usePermissions();
  // KAN-178: Call History can be the only voice surface mounted (Call Center
  // page, Call Manager history tab), so the store's pre-selected caller ID may
  // still be an admin-hidden number here. Mounting the hook moves it to a
  // visible one, and the auto-dial below refuses anything not in this list.
  const visibleCallerIds = useVisibleCallerIds();

  // Filter call history based on permissions
  const visibleCallHistory = useMemo(() => {
    if (!callPermissions.restrictToOwnCalls) return callHistory;
    // Only show calls where this user is the agent
    return callHistory.filter((item) => item.agentEmail === currentUserEmail);
  }, [callHistory, callPermissions.restrictToOwnCalls, currentUserEmail]);

  // All labels = defaults + custom
  const allLabels = useMemo(
    () => [...DEFAULT_CALL_LABELS, ...customCallLabels],
    [customCallLabels]
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState(''); // The term that was actually submitted
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
  const [filterDirection, setFilterDirection] = useState<DirectionFilter>('all');
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // CRM context — filter call history by CRM phone numbers
  const crmRecordContext = useUIStore((s) => s.crmRecordContext);
  const crmPhoneFields = useUIStore((s) => s.crmPhoneFields);
  const crmSelectedPhoneField = useUIStore((s) => s.crmSelectedPhoneField);

  // Context menu call number (from Chrome extension right-click → call)
  const contextMenuCallNumber = useCallStore((s) => s.contextMenuCallNumber);

  // In CRM context, use the selected/first CRM phone as the default search.
  // Also check contextMenuCallNumber (Chrome extension context menu → call-history route).
  const crmSearchPhone = useMemo(() => {
    // Context menu call number takes priority (from /call-history?callNumber=...)
    if (contextMenuCallNumber) return contextMenuCallNumber.replace(/\D/g, '');
    if (!crmRecordContext || crmPhoneFields.length === 0) return '';
    const selected = crmPhoneFields.find((f) => (f.key || f.apiName) === crmSelectedPhoneField);
    return (selected?.value || crmPhoneFields[0]?.value || '').replace(/\D/g, '');
  }, [crmRecordContext, crmPhoneFields, crmSelectedPhoneField, contextMenuCallNumber]);

  // Track whether initial load has fired
  const initialLoaded = useRef(false);

  // Build filter options object for API calls
  const buildFilterOptions = useCallback(
    (extra?: { search?: string; loadMore?: boolean }) => {
      const opts: Parameters<typeof fetchCallHistory>[0] = {};

      if (filterDirection !== 'all') opts.direction = filterDirection;
      if (filterStatus !== 'all') opts.status = filterStatus === 'no-answer' ? 'no-answer' : filterStatus;
      if (dateFrom) opts.dateFrom = dateFrom;
      if (dateTo) opts.dateTo = dateTo;

      // In CRM context, always filter by CRM phone number
      if (extra?.search) opts.search = extra.search;
      else if (crmSearchPhone) opts.search = crmSearchPhone;

      if (extra?.loadMore) opts.loadMore = true;

      return opts;
    },
    [filterDirection, filterStatus, dateFrom, dateTo, crmSearchPhone]
  );

  // Initial load when API is ready — in CRM context, pre-fill search with CRM phone
  useEffect(() => {
    if (api && accountSid && !initialLoaded.current) {
      initialLoaded.current = true;
      if (crmSearchPhone) {
        setSearchQuery(crmSearchPhone);
        setActiveSearch(crmSearchPhone);
      }
      fetchCallHistory(crmSearchPhone ? { search: crmSearchPhone } : undefined);
    }
  }, [fetchCallHistory, api, accountSid, crmSearchPhone]);

  // Fetch custom labels once
  useEffect(() => {
    fetchCustomCallLabels();
  }, [fetchCustomCallLabels]);

  // Lazily fetch metadata for visible calls
  useEffect(() => {
    if (callHistory.length > 0) {
      const sids = callHistory.map((c) => c.id);
      fetchCallMetadataBatch(sids);
    }
  }, [callHistory, fetchCallMetadataBatch]);

  // Re-fetch on filter change (direction / status / dates)
  const handleFilterChange = useCallback(
    (
      newDirection?: DirectionFilter,
      newStatus?: StatusFilter,
      newDateFrom?: string,
      newDateTo?: string
    ) => {
      const dir = newDirection ?? filterDirection;
      const stat = newStatus ?? filterStatus;
      const df = newDateFrom ?? dateFrom;
      const dt = newDateTo ?? dateTo;

      const opts: Parameters<typeof fetchCallHistory>[0] = {};
      if (dir !== 'all') opts.direction = dir;
      if (stat !== 'all') opts.status = stat === 'no-answer' ? 'no-answer' : stat;
      if (df) opts.dateFrom = df;
      if (dt) opts.dateTo = dt;

      const q = searchQuery.trim();
      if (q) opts.search = q;

      fetchCallHistory(opts);
    },
    [fetchCallHistory, filterDirection, filterStatus, dateFrom, dateTo, searchQuery]
  );

  // API search on Enter / button
  const handleApiSearch = useCallback(() => {
    const q = searchQuery.trim();
    setActiveSearch(q);
    fetchCallHistory(buildFilterOptions({ search: q || undefined }));
  }, [fetchCallHistory, buildFilterOptions, searchQuery]);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleApiSearch();
    },
    [handleApiSearch]
  );

  const handleLoadMore = useCallback(() => {
    fetchCallHistory(buildFilterOptions({ loadMore: true }));
  }, [fetchCallHistory, buildFilterOptions]);

  // "Call back" should place the call, not just prefill the dialer. Prefill-only
  // is kept as the fallback when the softphone isn't ready, a call is already
  // active, or the WhatsApp permission flow (which lives in the dialer) is needed.
  const handleCallBack = useCallback(
    (rawNumber: string) => {
      // WhatsApp-channel logs store "whatsapp:+91..." — dial the bare number
      const number = rawNumber.replace('whatsapp:', '');
      setDialerNumber(number);
      setCallManagerTab('dialer');

      const {
        softphoneState,
        selectedCallerId,
        callerIds,
        activeCalls,
        makeCall,
        crmCallManagerOpen,
        callManagerOpen,
        toggleCallManager,
      } = useCallStore.getState();
      const isWhatsAppCallerId =
        callerIds.find((c) => c.value === selectedCallerId)?.channel === 'WHATSAPP';
      // Ended/error calls are never removed from activeCalls, so only block
      // auto-dial while a call is actually in progress (incl. transferring).
      const callInProgress = activeCalls.some((c) => isLiveCallStatus(c.status));
      const canCallNow =
        softphoneState === 'registered' &&
        !!selectedCallerId &&
        visibleCallerIds.some((c) => c.value === selectedCallerId) &&
        !callInProgress &&
        !isWhatsAppCallerId &&
        callPermissions.canMakeOutboundCalls;

      if (canCallNow) {
        makeCall(number).catch((error) => {
          console.error('[CallHistory] Call back failed:', error);
        });
        return;
      }

      // Can't auto-dial — make sure a dialer showing the prefilled number is
      // visible. CrmCallManager's top bar and the Call Manager modal already
      // are; CallCenterView only has an inline dialer panel on lg+ screens.
      if (!crmCallManagerOpen && !callManagerOpen && !window.matchMedia('(min-width: 1024px)').matches) {
        toggleCallManager();
      }
    },
    [setDialerNumber, setCallManagerTab, callPermissions.canMakeOutboundCalls, visibleCallerIds]
  );

  const hasActiveFilters = filterDirection !== 'all' || filterStatus !== 'all' || !!dateFrom || !!dateTo;

  const clearAllFilters = useCallback(() => {
    setFilterDirection('all');
    setFilterStatus('all');
    setDateFrom('');
    setDateTo('');
    setSearchQuery('');
    setActiveSearch('');
    fetchCallHistory();
  }, [fetchCallHistory]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setActiveSearch('');
    fetchCallHistory(buildFilterOptions());
  }, [fetchCallHistory, buildFilterOptions]);

  const handleExportCSV = useCallback(() => {
    if (visibleCallHistory.length === 0) return;
    const headers = ['Date', 'Direction', 'Customer Number', 'Caller ID', 'Status', 'Duration (s)', 'Agent', 'Cost'];
    const rows = visibleCallHistory.map((item) => [
      item.time,
      item.isIncoming ? 'Inbound' : 'Outbound',
      item.customerNumber,
      item.companyCallerId,
      item.status,
      item.duration,
      item.agentEmail || '',
      item.price,
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [callHistory]);

  const dateLabel = (() => {
    if (dateFrom && dateTo) return `${dateFrom} → ${dateTo}`;
    if (dateFrom) return `From ${dateFrom}`;
    if (dateTo) return `Until ${dateTo}`;
    return '';
  })();

  return (
    <div className="flex flex-col h-full">
      {/* Search bar (hidden in compact mode) */}
      {!compact && <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
        <div className="flex-1 relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search by phone number..."
            className="w-full rounded-lg border border-border bg-muted/30 pl-8 pr-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <button
          onClick={() => setShowFilters((v) => !v)}
          className={cn(
            'shrink-0 rounded-lg p-1.5 transition-colors',
            showFilters || hasActiveFilters
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          )}
          title="Filters"
        >
          <Filter className="h-3.5 w-3.5" />
          {hasActiveFilters && !showFilters && (
            <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-primary" />
          )}
        </button>
        <button
          onClick={handleExportCSV}
          disabled={callHistory.length === 0}
          className="shrink-0 rounded-lg p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-30"
          title="Export as CSV"
        >
          <Download className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={handleApiSearch}
          disabled={callHistoryLoading}
          className="shrink-0 rounded-lg bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/20 transition-colors disabled:opacity-50"
          title="Search calls (Enter)"
        >
          {callHistoryLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Search'}
        </button>
      </div>}

      {!compact && <>
      {/* Filters row (collapsible) */}
      {showFilters && (
      <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-border overflow-x-auto">
        {/* Direction */}
        {(['all', 'inbound', 'outbound'] as const).map((val) => (
          <button
            key={val}
            onClick={() => {
              setFilterDirection(val);
              handleFilterChange(val, undefined, undefined, undefined);
            }}
            className={cn(
              'shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors',
              filterDirection === val
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            {val === 'all' ? 'All' : val === 'inbound' ? 'Incoming' : 'Outgoing'}
          </button>
        ))}

        <span className="w-px h-4 bg-border shrink-0 mx-0.5" />

        {/* Status */}
        {(['all', 'completed', 'no-answer', 'failed'] as const).map((val) => (
          <button
            key={val}
            onClick={() => {
              setFilterStatus(val);
              handleFilterChange(undefined, val, undefined, undefined);
            }}
            className={cn(
              'shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors',
              filterStatus === val
                ? val === 'no-answer'
                  ? 'bg-destructive text-destructive-foreground'
                  : val === 'failed'
                    ? 'bg-slate-600 text-white'
                    : 'bg-primary text-primary-foreground'
                : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            {val === 'all' ? 'Any Status' : val === 'completed' ? 'Completed' : val === 'no-answer' ? 'Missed' : 'Failed'}
          </button>
        ))}

        <span className="w-px h-4 bg-border shrink-0 mx-0.5" />

        {/* Date filter toggle */}
        <button
          onClick={() => setShowDatePicker((p) => !p)}
          className={cn(
            'shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors flex items-center gap-1',
            dateFrom || dateTo
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
          )}
        >
          <Calendar className="h-3 w-3" />
          {dateLabel || 'Date'}
        </button>

        {hasActiveFilters && (
          <>
            <span className="w-px h-4 bg-border shrink-0 mx-0.5" />
            <button
              onClick={clearAllFilters}
              className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex items-center gap-1"
            >
              <X className="h-3 w-3" />
              Clear
            </button>
          </>
        )}
      </div>
      )}

      {/* Date picker row (collapsed by default) */}
      {showFilters && showDatePicker && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/20">
          <span className="text-[11px] text-muted-foreground shrink-0">From</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              handleFilterChange(undefined, undefined, e.target.value, undefined);
            }}
            className="flex-1 min-w-0 rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <span className="text-[11px] text-muted-foreground shrink-0">To</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              handleFilterChange(undefined, undefined, undefined, e.target.value);
            }}
            className="flex-1 min-w-0 rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {(dateFrom || dateTo) && (
            <button
              onClick={() => {
                setDateFrom('');
                setDateTo('');
                handleFilterChange(undefined, undefined, '', '');
              }}
              className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title="Clear dates"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Search results banner — hidden in CRM context (auto-filtered by CRM phone) */}
      {activeSearch && !crmRecordContext && (
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border bg-muted/20">
          <span className="text-xs text-muted-foreground">
            Showing results for <span className="font-medium text-foreground">"{activeSearch}"</span>
          </span>
          <button
            onClick={clearSearch}
            className="ml-auto shrink-0 text-xs text-primary hover:underline"
          >
            Clear
          </button>
        </div>
      )}

      </>}

      {/* Call list */}
      <div className="flex-1 overflow-y-auto">
        {/* Skeleton on initial load */}
        {callHistoryLoading && callHistory.length === 0 && (
          <div className="animate-pulse">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2.5 border-b border-border/50">
                <div className="h-8 w-8 rounded-full bg-muted" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 w-28 rounded bg-muted" />
                  <div className="h-2.5 w-20 rounded bg-muted/70" />
                </div>
                <div className="h-5 w-16 rounded-full bg-muted" />
              </div>
            ))}
          </div>
        )}

        {callHistory.length === 0 && !callHistoryLoading && (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            {hasActiveFilters ? (
              <>
                <Filter className="h-8 w-8 mb-2 opacity-40" />
                <p className="text-sm">No calls match filters</p>
                <button
                  onClick={clearAllFilters}
                  className="mt-2 text-xs text-primary hover:underline"
                >
                  Clear all filters
                </button>
              </>
            ) : (
              <>
                <Clock className="h-8 w-8 mb-2 opacity-40" />
                <p className="text-sm">No call history</p>
              </>
            )}
          </div>
        )}

        {visibleCallHistory.map((item, idx) => {
          const next = visibleCallHistory[idx + 1];
          const hideBorder = !!next && next.customerNumber === item.customerNumber;
          return (
            <CallHistoryRow
              key={item.id}
              item={item}
              compact={compact}
              hasRecording={!compact && !!callsWithRecordings[item.id]}
              metadata={!compact ? callMetadata[item.id] : undefined}
              onUpdateMetadata={!compact ? (updates) => updateCallMetadata(item.id, updates, item.customerNumber) : undefined}
              allLabels={allLabels}
              isAdmin={isAdmin}
              onSaveCustomLabel={saveCustomCallLabel}
              expanded={false}
              hideBorder={hideBorder}
              hidePhoneButton={!!crmSearchPhone}
              stackSecondRow={!!contextMenuCallNumber}
              onToggle={() => !compact && setSelectedCallId(selectedCallId === item.id ? null : item.id)}
              onCallBack={() => handleCallBack(item.customerNumber)}
            />
          );
        })}

        {/* Spinner for load-more */}
        {callHistoryLoading && callHistory.length > 0 && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}

        {callHistoryHasMore && !callHistoryLoading && (
          <div className="flex justify-center py-3">
            <button
              onClick={handleLoadMore}
              className="flex items-center gap-1 rounded-lg px-4 py-2 text-xs font-medium text-primary hover:bg-primary/10 transition-colors"
            >
              <ChevronDown className="h-3.5 w-3.5" />
              Load More
            </button>
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selectedCallId && (() => {
        const selectedItem = callHistory.find((c) => c.id === selectedCallId);
        if (!selectedItem) return null;
        return (
          <CallDetailPanel
            item={selectedItem}
            onClose={() => setSelectedCallId(null)}
            metadata={callMetadata[selectedItem.id]}
            onUpdateMetadata={(updates) => updateCallMetadata(selectedItem.id, updates, selectedItem.customerNumber)}
            allLabels={allLabels}
          />
        );
      })()}
    </div>
  );
}

// ── Row Component ──────────────────────────────────────────────────────

function CallHistoryRow({
  item,
  compact,
  hasRecording,
  metadata,
  onUpdateMetadata,
  allLabels,
  isAdmin,
  onSaveCustomLabel,
  expanded,
  hideBorder,
  hidePhoneButton,
  stackSecondRow,
  onToggle,
  onCallBack,
}: {
  item: CallHistoryItem;
  compact?: boolean;
  hasRecording?: boolean;
  metadata?: import('@/types/voice').CallMetadata;
  onUpdateMetadata?: (updates: { label?: string | null; assignee?: string | null }) => void;
  allLabels: import('@/types/voice').CallLabel[];
  isAdmin?: boolean;
  onSaveCustomLabel?: (label: import('@/types/voice').CallLabel) => void;
  expanded: boolean;
  hideBorder?: boolean;
  hidePhoneButton?: boolean;
  stackSecondRow?: boolean;
  onToggle: () => void;
  onCallBack: () => void;
}) {
  const [labelPickerOpen, setLabelPickerOpen] = useState(false);
  const [creatingLabel, setCreatingLabel] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState(LABEL_COLOR_PALETTE[0]);
  const DirectionIcon = getDirectionIcon(item);
  const directionColor = getDirectionColor(item);
  const statusLabel = formatStatus(item.status);
  const durationStr = formatCallDuration(item.duration);
  const notesCount = metadata?.notesCount ?? 0;
  const timeStr = formatTime(item.time);

  const assigneeNode = !compact ? (
    <AssigneeCell
      assignee={metadata?.assignee ?? item.agentEmail}
      onAssign={(email) => onUpdateMetadata?.({ assignee: email })}
    />
  ) : null;

  const labelPickerNode = !compact ? (
    <div className="relative">
      {(() => {
        const activeLabel = allLabels.find((l) => l.value === metadata?.label);
        return (
          <button
            onClick={(e) => { e.stopPropagation(); setLabelPickerOpen((v) => !v); setCreatingLabel(false); }}
            className="rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors flex items-center gap-0.5"
            style={activeLabel
              ? { backgroundColor: activeLabel.color + '20', color: activeLabel.color }
              : undefined
            }
            title="Set label"
          >
            {activeLabel ? activeLabel.label : <><Tag className="h-3 w-3 text-muted-foreground/40" /><span className="text-muted-foreground/50">No Label</span></>}
            <ChevronDown className="h-2.5 w-2.5 opacity-50" />
          </button>
        );
      })()}
      {labelPickerOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setLabelPickerOpen(false); }} />
          <div className="absolute top-full right-0 mt-1 z-50 w-44 rounded-lg border border-border bg-card shadow-lg py-1">
            {metadata?.label && (
              <button
                onClick={(e) => { e.stopPropagation(); onUpdateMetadata?.({ label: null }); setLabelPickerOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-[11px] text-muted-foreground hover:bg-muted/50"
              >
                Remove label
              </button>
            )}
            {allLabels.map((l) => (
              <button
                key={l.value}
                onClick={(e) => { e.stopPropagation(); onUpdateMetadata?.({ label: l.value }); setLabelPickerOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-medium hover:bg-muted/50 transition-colors"
              >
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: l.color }} />
                <span style={metadata?.label === l.value ? { color: l.color } : undefined}>
                  {l.label}
                </span>
              </button>
            ))}

            {/* Create custom label (admin only) */}
            {isAdmin && !creatingLabel && (
              <>
                <div className="border-t border-border my-1" />
                <button
                  onClick={(e) => { e.stopPropagation(); setCreatingLabel(true); }}
                  className="w-full text-left px-3 py-1.5 text-[11px] text-primary hover:bg-muted/50"
                >
                  + Create label
                </button>
              </>
            )}

            {isAdmin && creatingLabel && (
              <div className="border-t border-border mt-1 px-3 py-2 space-y-2" onClick={(e) => e.stopPropagation()}>
                <input
                  type="text"
                  value={newLabelName}
                  onChange={(e) => setNewLabelName(e.target.value)}
                  placeholder="Label name"
                  className="w-full rounded border border-border bg-muted/30 px-2 py-1 text-[11px] text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newLabelName.trim()) {
                      const val = newLabelName.trim().toLowerCase().replace(/\s+/g, '-');
                      onSaveCustomLabel?.({ value: val, label: newLabelName.trim(), color: newLabelColor, isCustom: true });
                      setNewLabelName('');
                      setCreatingLabel(false);
                    }
                  }}
                />
                <div className="flex flex-wrap gap-1">
                  {LABEL_COLOR_PALETTE.map((c) => (
                    <button
                      key={c}
                      onClick={() => setNewLabelColor(c)}
                      className={cn(
                        'h-5 w-5 rounded-full transition-all',
                        newLabelColor === c ? 'ring-2 ring-offset-1 ring-foreground/30 scale-110' : 'hover:scale-110'
                      )}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setCreatingLabel(false)}
                    className="flex-1 rounded px-2 py-1 text-[10px] text-muted-foreground hover:bg-muted"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (newLabelName.trim()) {
                        const val = newLabelName.trim().toLowerCase().replace(/\s+/g, '-');
                        onSaveCustomLabel?.({ value: val, label: newLabelName.trim(), color: newLabelColor, isCustom: true });
                        setNewLabelName('');
                        setCreatingLabel(false);
                      }
                    }}
                    disabled={!newLabelName.trim()}
                    className="flex-1 rounded bg-primary px-2 py-1 text-[10px] font-medium text-primary-foreground disabled:opacity-50"
                  >
                    Create
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  ) : null;

  return (
    <div className={cn('last:border-0', hideBorder ? '' : 'border-b border-border/50')}>
      {/* Main row */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onToggle(); }}
        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/30 transition-colors text-left cursor-pointer"
      >
        <div
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
            directionColor.bg
          )}
        >
          <DirectionIcon className={cn('h-4 w-4', directionColor.text)} />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate flex items-center gap-1.5">
            {item.callChannel === 'whatsapp' && (
              <MessageCircle className="h-3 w-3 text-green-500 shrink-0" />
            )}
            {item.customerNumber?.replace('whatsapp:', '')}
          </p>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            {item.callChannel === 'whatsapp' && (
              <>
                <span className="text-green-600 dark:text-green-400 font-medium">WhatsApp</span>
                <span className="text-border">|</span>
              </>
            )}
            <span>{timeStr}</span>
            {item.agentEmail && (
              <>
                <span className="text-border">|</span>
                <span className="truncate max-w-[80px]">{item.agentEmail.split('@')[0]}</span>
              </>
            )}
            {durationStr && (
              <>
                <span className="text-border">|</span>
                <span>{durationStr}</span>
              </>
            )}
          </div>
          {/* Stacked second line: assignee + label (used when display is narrow, e.g. appcursor context menu) */}
          {stackSecondRow && !compact && (
            <div className="flex items-center gap-2 mt-1.5">
              {assigneeNode}
              {labelPickerNode}
            </div>
          )}
        </div>

        {/* Inline: icons + label + notes count (hidden in compact) */}
        {!compact && (
        <div className="flex items-center gap-1 shrink-0">
          {hasRecording && (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500/15" title="Has recording">
              <Mic className="h-3 w-3 text-red-500" />
            </span>
          )}
          <span
            className={cn(
              'flex items-center gap-0.5 rounded-full px-1.5 py-0.5',
              notesCount > 0 ? 'bg-amber-500/15' : 'opacity-30'
            )}
            title={notesCount > 0 ? `${notesCount} note${notesCount > 1 ? 's' : ''}` : 'No notes'}
          >
            <StickyNote className={cn('h-3 w-3', notesCount > 0 ? 'text-amber-500' : 'text-muted-foreground')} />
            {notesCount > 0 && (
              <span className="text-[10px] font-medium text-amber-600 dark:text-amber-400">{notesCount}</span>
            )}
          </span>
        </div>
        )}

        {/* Assignee avatar (inline when not stacked) */}
        {!stackSecondRow && assigneeNode}

        <div className="flex items-center gap-1 shrink-0">
          {/* Label picker (inline when not stacked) */}
          {!stackSecondRow && labelPickerNode}
          {!hidePhoneButton && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCallBack();
              }}
              className="rounded-full p-1.5 text-muted-foreground hover:text-emerald-500 hover:bg-emerald-500/10 transition-colors"
              title="Call back"
            >
              <Phone className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

    </div>
  );
}

// ── Agent Cell ──────────────────────────────────────────────────────────

function AgentCell({ email }: { email: string | null }) {
  const orgUsers = useAppUsersStore((s) => s.orgUsers);
  const currentUser = useAuthStore((s) => s.user);

  if (!email) {
    return (
      <div className="hidden sm:block w-28 shrink-0">
        <span className="text-[11px] text-muted-foreground/40">—</span>
      </div>
    );
  }

  // Check org users first, then fall back to current logged-in user
  const orgUser = Object.values(orgUsers).find((u) => u.email === email);
  const isCurrentUser = !orgUser && currentUser?.email === email;
  const name = orgUser?.name || (isCurrentUser ? (currentUser?.displayName || email.split('@')[0]) : email.split('@')[0]);
  const picture = orgUser?.picture || (isCurrentUser ? currentUser?.photoURL : undefined);
  const initial = (name[0] || email[0] || '?').toUpperCase();

  // Deterministic color from email hash
  const AVATAR_COLORS = [
    { bg: 'bg-blue-500/15', text: 'text-blue-600 dark:text-blue-400' },
    { bg: 'bg-emerald-500/15', text: 'text-emerald-600 dark:text-emerald-400' },
    { bg: 'bg-violet-500/15', text: 'text-violet-600 dark:text-violet-400' },
    { bg: 'bg-amber-500/15', text: 'text-amber-600 dark:text-amber-400' },
    { bg: 'bg-rose-500/15', text: 'text-rose-600 dark:text-rose-400' },
    { bg: 'bg-cyan-500/15', text: 'text-cyan-600 dark:text-cyan-400' },
    { bg: 'bg-orange-500/15', text: 'text-orange-600 dark:text-orange-400' },
    { bg: 'bg-indigo-500/15', text: 'text-indigo-600 dark:text-indigo-400' },
  ];
  const colorIdx = email.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % AVATAR_COLORS.length;
  const avatarColor = AVATAR_COLORS[colorIdx];

  return (
    <div className="hidden sm:flex items-center gap-1.5 w-28 shrink-0" title={email}>
      {picture ? (
        <img
          src={picture}
          alt=""
          className="h-5 w-5 rounded-full object-cover shrink-0"
        />
      ) : (
        <div className={cn('h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-semibold shrink-0', avatarColor.bg, avatarColor.text)}>
          {initial}
        </div>
      )}
      <span className="text-[11px] text-muted-foreground truncate">
        {name}
      </span>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────

function getDirectionIcon(item: CallHistoryItem) {
  if (item.status === 'no-answer' || item.status === 'missed') return PhoneMissed;
  if (item.status === 'failed') return item.isIncoming ? PhoneMissed : PhoneOutgoing;
  return item.isIncoming ? PhoneIncoming : PhoneOutgoing;
}

function getDirectionColor(item: CallHistoryItem) {
  if (item.status === 'no-answer' || item.status === 'missed') {
    return { bg: 'bg-destructive/10', text: 'text-destructive' };
  }
  if (item.status === 'failed') {
    return { bg: 'bg-slate-500/10', text: 'text-slate-500' };
  }
  if (item.isIncoming) {
    return { bg: 'bg-blue-500/10', text: 'text-blue-500' };
  }
  return { bg: 'bg-emerald-500/10', text: 'text-emerald-500' };
}

function getStatusColor(status: string) {
  switch (status) {
    case 'completed':
      return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400';
    case 'busy':
      return 'bg-amber-500/10 text-amber-600 dark:text-amber-400';
    case 'no-answer':
    case 'missed':
      return 'bg-destructive/10 text-destructive';
    case 'failed':
    case 'canceled':
      return 'bg-slate-500/10 text-slate-500';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function formatStatus(status: string): string {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'no-answer':
      return 'No Answer';
    case 'missed':
      return 'Missed';
    case 'busy':
      return 'Busy';
    case 'failed':
      return 'Failed';
    case 'canceled':
      return 'Canceled';
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

function formatCallDuration(duration: string): string {
  const sec = parseInt(duration) || 0;
  if (sec === 0) return '';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

// ── Assignee Cell (inline avatar with reassign dropdown) ─────────

const AVATAR_COLORS = [
  { bg: 'bg-blue-500/15', text: 'text-blue-600 dark:text-blue-400' },
  { bg: 'bg-emerald-500/15', text: 'text-emerald-600 dark:text-emerald-400' },
  { bg: 'bg-violet-500/15', text: 'text-violet-600 dark:text-violet-400' },
  { bg: 'bg-amber-500/15', text: 'text-amber-600 dark:text-amber-400' },
  { bg: 'bg-rose-500/15', text: 'text-rose-600 dark:text-rose-400' },
  { bg: 'bg-cyan-500/15', text: 'text-cyan-600 dark:text-cyan-400' },
  { bg: 'bg-orange-500/15', text: 'text-orange-600 dark:text-orange-400' },
  { bg: 'bg-indigo-500/15', text: 'text-indigo-600 dark:text-indigo-400' },
];

function getAvatarColor(email: string) {
  const idx = email.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
}

function AssigneeCell({ assignee, onAssign }: { assignee: string | null; onAssign: (email: string | null) => void }) {
  const orgUsers = useAppUsersStore((s) => s.orgUsers);
  const currentUser = useAuthStore((s) => s.user);
  const [open, setOpen] = useState(false);

  const userList = useMemo(() => {
    const list = Object.values(orgUsers).map((u) => ({ email: u.email, name: u.name, picture: u.picture }));
    if (currentUser?.email && !list.some((u) => u.email === currentUser.email)) {
      list.unshift({ email: currentUser.email, name: currentUser.displayName || 'You', picture: currentUser.photoURL || undefined });
    }
    return list;
  }, [orgUsers, currentUser]);

  const assigneeUser = assignee ? userList.find((u) => u.email === assignee) : null;
  const name = assigneeUser?.name || (assignee ? assignee.split('@')[0] : '');
  const picture = assigneeUser?.picture;
  const initial = (name[0] || assignee?.[0] || '?').toUpperCase();
  const color = assignee ? getAvatarColor(assignee) : null;

  return (
    <div className="hidden sm:block relative shrink-0">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="flex items-center gap-1.5 w-28 rounded-md px-1 py-0.5 hover:bg-muted/50 transition-colors"
        title={assignee ? `Assigned to ${name} — click to reassign` : 'Unassigned — click to assign'}
      >
        {assignee && picture ? (
          <img src={picture} alt="" className="h-5 w-5 rounded-full object-cover shrink-0" />
        ) : assignee && color ? (
          <div className={cn('h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-semibold shrink-0', color.bg, color.text)}>
            {initial}
          </div>
        ) : (
          <div className="h-5 w-5 rounded-full bg-muted/50 flex items-center justify-center shrink-0">
            <span className="text-[9px] text-muted-foreground/40">?</span>
          </div>
        )}
        <span className="text-[11px] text-muted-foreground truncate">
          {name || 'Unassigned'}
        </span>
        <ChevronDown className="h-2.5 w-2.5 text-muted-foreground/50 shrink-0" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div className="absolute top-full right-0 mt-1 z-50 w-52 rounded-lg border border-border bg-card shadow-lg max-h-48 overflow-y-auto">
            {assignee && (
              <button
                onClick={(e) => { e.stopPropagation(); onAssign(null); setOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted/50"
              >
                Remove assignee
              </button>
            )}
            {userList.map((u) => {
              const uColor = getAvatarColor(u.email);
              return (
                <button
                  key={u.email}
                  onClick={(e) => { e.stopPropagation(); onAssign(u.email); setOpen(false); }}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted/50 transition-colors',
                    assignee === u.email && 'bg-primary/5 text-primary'
                  )}
                >
                  {u.picture ? (
                    <img src={u.picture} alt="" className="h-4 w-4 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className={cn('h-4 w-4 rounded-full flex items-center justify-center text-[8px] font-semibold shrink-0', uColor.bg, uColor.text)}>
                      {(u.name?.[0] || u.email[0] || '?').toUpperCase()}
                    </div>
                  )}
                  <span className="truncate">{u.name || u.email.split('@')[0]}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function formatTime(time: string): string {
  if (!time) return '';
  try {
    const date = new Date(time);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    return date.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return time;
  }
}
